import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, Users } from 'lucide-react';
// Fase 5.17 -- `useCurrentUser`/`useCurrentSpace` son selectores zustand a nivel de módulo, NO
// React context (misma excepción documentada que 5.10/5.16). `toast` llega por prop desde
// `TaskHoyToolbar` (← `ComponentTaskHoy`).
// eslint-disable-next-line no-restricted-imports
import { useCurrentUser, useCurrentSpace } from '../../../context/authHooks';
import { useTaskFilterUsers } from '../../../shared/hooks/useTaskFilterUsers';
import { useAllUsers } from '../../../shared/hooks/useAllUsers';
import { useTeamMembers } from '../../settings/hooks/useTeam';
import './ComponentTask.css';

// Filtro global de tareas por usuario (facepile + popover). Integración main→dev, epic C2:
// `main` lo movió de la barra lateral (`ComponentTask.jsx`) al header de la vista de tareas.
// Widget con estado + persistencia propios (`useTaskFilterStore` / `useTaskFilterUsers`, POST a
// `/api/customs`) y toast propio al guardar — igual que la versión de `main`.
export const TaskUserFilter = ({ toast }) => {
    const { t } = useTranslation('tasks');
    const { dbId } = useCurrentUser();
    const { currentSpace } = useCurrentSpace();
    const { taskFilterUsers, setTaskFilterUsers } = useTaskFilterUsers(dbId);

    const activeSpace = currentSpace || null;
    const { data: allUsers = [] } = useAllUsers();
    const { data: spaceMembersData = [] } = useTeamMembers(activeSpace?.id);
    const filterUsersPool = activeSpace?.id
        ? spaceMembersData.map(m => ({ ...m, nick: m.nick || m.name }))
        : allUsers;

    const [searchQuery, setSearchQuery] = useState('');
    const [showDropdown, setShowDropdown] = useState(false);
    const [focusedIndex, setFocusedIndex] = useState(-1);
    const containerRef = useRef(null);

    const activeTaskFilterUsers = useMemo(
        () => taskFilterUsers.filter(tu => filterUsersPool.some(pu => pu.id === tu.id)),
        [taskFilterUsers, filterUsersPool],
    );

    const filteredAutocompleteUsers = filterUsersPool.filter(u =>
        u.nick && u.nick.toLowerCase().includes(searchQuery.toLowerCase()),
    );

    useEffect(() => {
        if (!showDropdown) return undefined;
        const handleClickOutside = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) setShowDropdown(false);
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [showDropdown]);

    const saveFilter = (list) => {
        setTaskFilterUsers(list);
        const isAll = list.length === filterUsersPool.length && filterUsersPool.length > 0;
        toast.success(isAll ? t('filter.savedAll') : t('filter.savedCount', { count: list.length }));
    };

    const toggleUserFilter = (u) => {
        const exists = taskFilterUsers.some(user => user.id === u.id);
        saveFilter(exists ? taskFilterUsers.filter(user => user.id !== u.id) : [...taskFilterUsers, u]);
    };

    const handleKeyDown = (e) => {
        if (!showDropdown) return;
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setFocusedIndex(prev => (prev < Math.min(50, filteredAutocompleteUsers.length) - 1 ? prev + 1 : prev));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setFocusedIndex(prev => (prev > -1 ? prev - 1 : -1));
        } else if (e.key === 'Enter') {
            e.preventDefault();
            if (focusedIndex >= 0 && focusedIndex < filteredAutocompleteUsers.length) {
                toggleUserFilter(filteredAutocompleteUsers[focusedIndex]);
            } else if (focusedIndex === -1 || searchQuery.toLowerCase() === 'todos') {
                saveFilter([]);
                setSearchQuery('');
            }
        } else if (e.key === 'Escape') {
            setShowDropdown(false);
        }
    };

    const allSelected = taskFilterUsers.length === filterUsersPool.length && filterUsersPool.length > 0;

    return (
        <div className="task-filter-autocomplete-wrapper task-filter-in-header" ref={containerRef}>
            <div
                className="task-filter-facepile"
                onClick={() => {
                    setShowDropdown(!showDropdown);
                    if (!showDropdown) setFocusedIndex(-1);
                }}
            >
                {activeTaskFilterUsers.length > 0 ? (
                    activeTaskFilterUsers.slice(0, 4).map((u, i) => (
                        <img
                            key={u.id}
                            src={(u['link-profile-img-thumb'] || u['link-profile-img'] || u.image) || `https://ui-avatars.com/api/?name=${encodeURIComponent(u.nick || 'U')}`}
                            alt={u.nick}
                            className="task-filter-facepile-avatar"
                            style={{ zIndex: 10 - i, marginLeft: i === 0 ? 0 : '-10px' }}
                            loading="lazy"
                        />
                    ))
                ) : (
                    <div className="task-filter-empty-trigger">
                        <Users size={14} />
                    </div>
                )}
                {activeTaskFilterUsers.length > 4 && (
                    <div className="task-filter-facepile-more">+{activeTaskFilterUsers.length - 4}</div>
                )}
            </div>

            {showDropdown && (
                <div className="task-filter-dropdown" onClick={(e) => e.stopPropagation()}>
                    <div className="task-filter-input-container">
                        <Search size={12} className="task-filter-search-icon" />
                        <input
                            type="text"
                            className="task-filter-input"
                            placeholder={t('sidebar.filterSearchPlaceholder')}
                            value={searchQuery}
                            onChange={(e) => { setSearchQuery(e.target.value); setFocusedIndex(-1); }}
                            onKeyDown={handleKeyDown}
                            autoFocus
                        />
                    </div>

                    <div className="task-filter-dropdown-list">
                        <div
                            className={`task-filter-dropdown-item ${focusedIndex === -1 ? 'focused' : ''}`}
                            onClick={() => { saveFilter(allSelected ? [] : filterUsersPool); setSearchQuery(''); }}
                            onMouseEnter={() => setFocusedIndex(-1)}
                        >
                            <span style={{ fontWeight: '600', flex: 1 }}>
                                {allSelected ? t('sidebar.clearSelection') : t('sidebar.selectAll')}
                            </span>
                            <div className={`task-filter-togger ${allSelected ? 'active' : ''}`}>
                                <div className="task-filter-togger-knob"></div>
                            </div>
                        </div>

                        {filteredAutocompleteUsers.slice(0, 50).map((u, index) => {
                            const isSelected = taskFilterUsers.some(su => su.id === u.id);
                            return (
                                <div
                                    key={u.id}
                                    className={`task-filter-dropdown-item ${focusedIndex === index ? 'focused' : ''}`}
                                    onClick={() => toggleUserFilter(u)}
                                    onMouseEnter={() => setFocusedIndex(index)}
                                >
                                    <img
                                        src={(u['link-profile-img-thumb'] || u['link-profile-img'] || u.image) || `https://ui-avatars.com/api/?name=${encodeURIComponent(u.nick || 'U')}`}
                                        alt={u.nick}
                                        className="task-filter-dropdown-avatar"
                                        loading="lazy"
                                    />
                                    <span style={{ flex: 1 }}>{u.nick}</span>
                                    <div className={`task-filter-togger ${isSelected ? 'active' : ''}`}>
                                        <div className="task-filter-togger-knob"></div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
};

export default TaskUserFilter;
